/**
 * /earthworks/sites/:id — one site (GET / PATCH / DELETE /earthworks/sites/:id).
 *
 * Panels stack full width: details, the edit form when editing (never a
 * modal), then the site's jobs. Edit and delete are admin / office staff.
 *
 * A site with an open job (draft, scheduled or in progress) cannot be deleted:
 * the API answers 409 `site_has_live_jobs` naming the jobs, and the dialog
 * says so in words, naming them — never a green toast and never a bare 409.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Activity, ClipboardList, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDeleteEwSite, useEwCustomers, useEwMeta, useEwSite, useUpdateEwSite } from '../../hooks/useEarthworks';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canAdminEw, canDispatchEw, ewDay, ewJobStatusSkin, ewRefusal, ewStatusSkin, fmtNumber } from '../../lib/earthworks';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useBackToList, useOpenRecord } from '../downhole/recordPage';
import { EwSiteForm } from './SiteForm';
import { LocatesSection } from './LocatesSection';
import { isNotFound, EW_EYEBROW, EW_PRIMARY, EW_SECONDARY, EwBadge, EwDialog, EwEmpty, EwFormError, EwLoading, EwPanel, EwSpec } from './EwBits';

export default function EwSiteDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const back = useBackToList('/earthworks/sites');
  const { user } = useAuth();
  const query = useEwSite(id);
  const catalog = useEwMeta().data?.data ?? null;
  const canEdit = canAdminEw(user?.role);
  const customers = useEwCustomers(canEdit);
  const update = useUpdateEwSite();
  const del = useDeleteEwSite();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const site = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const jobs = site?.jobs ?? [];
  const openJobs = jobs.filter((j) => j.status === 'draft' || j.status === 'scheduled' || j.status === 'in_progress');
  const kindLabel = site ? site.kindLabel ?? site.kind : null;
  const addressLine = site ? [site.address, site.city, site.provinceState, site.postalCode].filter(Boolean).join(', ') : '';

  return (
    <div data-testid="ew-site-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={kindLabel ? `${EW_EYEBROW} · ${kindLabel}` : EW_EYEBROW}
        title={site?.name || (query.isLoading ? 'Site' : '—')}
        onBack={back}
        backLabel="Back"
        actions={
          site && canEdit ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="fi-action fi-action--standard fi-action--primary"
                onClick={() => { setEditError(null); setEditing(true); }}
                disabled={editing}
              >
                <Pencil className="size-4" aria-hidden="true" />
                Edit site
              </button>
              <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </button>
            </div>
          ) : undefined
        }
        stats={[
          {
            icon: <Activity className="size-4" aria-hidden="true" />,
            label: 'Status',
            tone: site?.status === 'active' ? 'success' : 'neutral',
            loading: query.isLoading,
            value: site ? ewStatusSkin(site.status).label : '—',
          },
          {
            icon: <ClipboardList className="size-4" aria-hidden="true" />,
            label: 'Jobs',
            tone: 'neutral',
            loading: query.isLoading,
            value: site ? displayValue(jobs.length) : '—',
          },
          {
            icon: <MapPin className="size-4" aria-hidden="true" />,
            label: 'Open jobs',
            tone: openJobs.length > 0 ? 'accent' : 'neutral',
            loading: query.isLoading,
            value: site ? displayValue(openJobs.length) : '—',
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <EwLoading label="Loading site" />
          ) : notFound || (!failure && !site) ? (
            <EwPanel eyebrow="Site" title="Site">
              <EwEmpty>This site was not found. It may have been deleted.</EwEmpty>
            </EwPanel>
          ) : failure || !site ? (
            <EwPanel eyebrow="Site" title="Site">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this site" />
            </EwPanel>
          ) : (
            <>
              <EwPanel eyebrow={kindLabel ?? 'Site'} title="Details" testId="ew-site-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <EwSpec label="Customer" value={site.customerName || '—'} />
                  <EwSpec label="Kind" value={kindLabel || '—'} />
                  <EwSpec label="Status" value={<EwBadge skin={ewStatusSkin(site.status)} value={site.status} />} />
                  <EwSpec label="Address" value={addressLine || '—'} />
                  <EwSpec label="Province / state" value={site.provinceName || site.provinceState || '—'} />
                  <EwSpec label="Legal land / LSD" value={site.legalLand || '—'} mono />
                  <EwSpec
                    label="Coordinates"
                    value={site.latitude != null && site.longitude != null ? `${fmtNumber(site.latitude)}, ${fmtNumber(site.longitude)}` : '—'}
                    mono
                  />
                  <EwSpec label="Access notes" value={site.accessNotes || '—'} />
                </dl>
                {site.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{site.notes}</p>}
              </EwPanel>

              {editing && canEdit && (
                <EwPanel eyebrow="Edit" title="Edit site" testId="ew-site-edit">
                  <EwSiteForm
                    site={site}
                    catalog={catalog}
                    meta={meta}
                    customers={customers.data ?? []}
                    pending={update.isPending}
                    error={editError}
                    submitLabel="Save site"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) =>
                      update.mutate(
                        { id: site.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Site saved');
                            setEditing(false);
                          },
                          onError: (err) => setEditError(ewRefusal(err, 'The site could not be saved.')),
                        },
                      )
                    }
                  />
                </EwPanel>
              )}

              <LocatesSection siteId={site.id} jobs={jobs} canWrite={canDispatchEw(user?.role)} />

              <EwPanel
                eyebrow="Work"
                title="Jobs at this site"
                testId="ew-site-jobs"
                actions={
                  canDispatchEw(user?.role) ? (
                    <button type="button" className={EW_SECONDARY} onClick={() => openRecord(`/earthworks/jobs/new?siteId=${site.id}&customerId=${site.customerId}`)}>
                      <Plus className="size-4" aria-hidden="true" />
                      New job
                    </button>
                  ) : undefined
                }
              >
                {jobs.length === 0 ? (
                  <EwEmpty>No jobs at this site yet.</EwEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {jobs.map((j) => (
                      <li key={j.id}>
                        <button
                          type="button"
                          data-testid="ew-site-job"
                          onClick={() => openRecord(`/earthworks/jobs/${j.id}`)}
                          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/5"
                        >
                          <span className="min-w-0">
                            <span className="block font-mono text-sm font-extrabold text-white">{j.jobNumber || '—'}</span>
                            <span className="block text-xs text-fieldiq-muted">
                              {j.jobTypeLabel || '—'} · {ewDay(j.scheduledStart)}
                            </span>
                          </span>
                          <EwBadge skin={ewJobStatusSkin(j.status)} value={j.status} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </EwPanel>
            </>
          )}
        </div>
      </section>

      {site && canEdit && (
        <EwDialog
          open={deleting}
          title="Delete site"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={EW_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep site
              </button>
              <button
                type="button"
                className={EW_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(site.id, {
                    onSuccess: () => {
                      toast.success('Site deleted');
                      navigate('/earthworks/sites', { replace: true });
                    },
                    onError: (err) => setDeleteError(ewRefusal(err, 'The site could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete site'}
              </button>
            </>
          }
        >
          <EwFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">Removes {site.name} from the register.</p>
        </EwDialog>
      )}
    </div>
  );
}
